<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import eventService from '../services/event.service'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

// Event data
const event = ref<any>(null)
const participants = ref<any>(null)
const attendance = ref<any>(null)

// Loading states
const loading = ref(true)
const loadingParticipants = ref(false)
const loadingAttendance = ref(false)

// Get event UUID and date from route
const eventUuid = computed(() => route.params.uuid as string)
const eventDate = computed(() => route.query.date as string)
const clubUuid = computed(() => authStore.initData?.club?.uuid || null)

// Get current role
const currentRole = computed(() => authStore.initData?.persons?.current?.role || null)

// Check permissions
const hasPermission = (permission: string) => {
  const permissions = authStore.initData?.permission_model || []
  return permissions.includes(permission)
}

const canUpdate = computed(() => hasPermission('EVENT_UPDATE'))
const canAttendanceStore = computed(() => hasPermission('EVENT_ATTENDANCE_STORE'))
const canAttendanceUpdate = computed(() => hasPermission('EVENT_ATTENDANCE_UPDATE'))

// Computed attendance stats
const presentCoaches = computed(() => {
  if (!attendance.value?.coaches) return []
  return attendance.value.coaches.filter((c: any) => c.is_present)
})

const absentCoaches = computed(() => {
  if (!attendance.value?.coaches) return []
  return attendance.value.coaches.filter((c: any) => !c.is_present)
})

const presentPlayers = computed(() => {
  if (!attendance.value?.players) return []
  return attendance.value.players.filter((p: any) => p.is_present)
})

const absentPlayers = computed(() => {
  if (!attendance.value?.players) return []
  return attendance.value.players.filter((p: any) => !p.is_present)
})

// Fetch event data
const fetchEvent = async () => {
  if (!clubUuid.value || !eventUuid.value) return

  loading.value = true
  try {
    const response = await eventService.getEvent(clubUuid.value, eventUuid.value, eventDate.value)
    event.value = response
  } catch (err: any) {
    console.error('Error fetching event:', err)
    toastStore.error(t('eventDetail.errorFetchingEvent'))
    router.push('/events')
  } finally {
    loading.value = false
  }
}

// Fetch participants
const fetchParticipants = async () => {
  if (!clubUuid.value || !eventUuid.value) return

  loadingParticipants.value = true
  try {
    const response = await eventService.getParticipants(clubUuid.value, eventUuid.value, eventDate.value)
    participants.value = response
  } catch (err: any) {
    console.error('Error fetching participants:', err)
  } finally {
    loadingParticipants.value = false
  }
}

// Fetch attendance
const fetchAttendance = async () => {
  if (!clubUuid.value || !eventUuid.value) return
  if (!event.value?.has_attendance) return

  loadingAttendance.value = true
  try {
    const response = await eventService.getAttendance(clubUuid.value, eventUuid.value)
    attendance.value = response
  } catch (err: any) {
    console.error('Error fetching attendance:', err)
  } finally {
    loadingAttendance.value = false
  }
}

const goBack = () => {
  router.push('/events')
}

onMounted(async () => {
  await fetchEvent()

  // Only fetch attendance and participants if not PLAYER
  if (currentRole.value !== 'PLAYER') {
    if (event.value?.has_attendance) {
      await fetchAttendance()
    } else {
      await fetchParticipants()
    }
  }
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-4 py-2">
      <!-- Back Button -->
      <div class="mb-4">
        <button @click="goBack" class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('eventDetail.backToEvents') }}
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <p class="mt-2 text-gray-600">{{ t('eventDetail.loadingEvent') }}</p>
      </div>

      <template v-else-if="event">
        <!-- Page Header -->
        <div class="mb-4 flex flex-wrap justify-between items-start gap-4">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">{{ event.title }}</h1>
            <p class="text-sm text-gray-600 mt-1">{{ event.category_type }}</p>
          </div>
          <div class="flex gap-2">
            <router-link
              v-if="event.has_attendance && canAttendanceUpdate"
              :to="`/events/${eventUuid}/attendance/edit?date=${eventDate}`"
              class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 border-2 border-blue-600"
            >
              {{ t('eventDetail.editAttendance') }}
            </router-link>
            <router-link
              v-else-if="!event.has_attendance && canAttendanceStore"
              :to="`/events/${eventUuid}/attendance/add?date=${eventDate}`"
              class="px-4 py-2 bg-green-600 text-sm font-medium text-white rounded hover:bg-green-700 border-2 border-green-600"
            >
              {{ t('eventDetail.checkAttendance') }}
            </router-link>
            <router-link
              v-if="canUpdate"
              :to="`/events/${eventUuid}/edit?date=${eventDate}`"
              class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
            >
              {{ t('eventDetail.editEvent') }}
            </router-link>
          </div>
        </div>

        <!-- Event Details Card -->
        <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-6 mb-4">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ t('eventDetail.eventDetails') }}</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span class="font-semibold text-gray-700">{{ t('eventDetail.place') }}:</span>
              <span class="text-gray-900 ml-2">{{ event.place || '-' }}</span>
            </div>
            <div>
              <span class="font-semibold text-gray-700">{{ t('eventDetail.day') }}:</span>
              <span class="text-gray-900 ml-2">{{ event.day }}</span>
            </div>
            <div>
              <span class="font-semibold text-gray-700">{{ t('eventDetail.start') }}:</span>
              <span class="text-gray-900 ml-2">{{ event.start }}</span>
            </div>
            <div>
              <span class="font-semibold text-gray-700">{{ t('eventDetail.end') }}:</span>
              <span class="text-gray-900 ml-2">{{ event.end }}</span>
            </div>
          </div>
        </div>

        <!-- Attendance View (when attendance is checked) -->
        <template v-if="event.has_attendance && attendance">
          <!-- Attendance Summary Cards (hidden for PLAYER role) -->
          <div v-if="currentRole !== 'PLAYER'" class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
              <div class="text-sm text-gray-600 mb-1">{{ t('eventDetail.coachesPresent') }}</div>
              <div class="text-2xl font-bold text-gray-900">{{ presentCoaches.length }} / {{ attendance.coaches?.length || 0 }}</div>
            </div>
            <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
              <div class="text-sm text-gray-600 mb-1">{{ t('eventDetail.playersPresent') }}</div>
              <div class="text-2xl font-bold text-green-600">{{ presentPlayers.length }}</div>
            </div>
            <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">
              <div class="text-sm text-gray-600 mb-1">{{ t('eventDetail.playersAbsent') }}</div>
              <div class="text-2xl font-bold text-red-600">{{ absentPlayers.length }}</div>
            </div>
          </div>

          <!-- Coaches Section -->
          <div v-if="attendance.coaches && attendance.coaches.length > 0" class="bg-white border-2 border-gray-300 rounded shadow-sm p-6 mb-4">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ t('eventDetail.coaches') }}</h2>

            <!-- Present Coaches -->
            <div v-if="presentCoaches.length > 0" class="mb-4">
              <h3 class="text-sm font-semibold text-green-700 mb-2">{{ t('eventDetail.present') }} ({{ presentCoaches.length }})</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                <div v-for="coach in presentCoaches" :key="coach.coach_uuid" class="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded">
                  <svg class="w-5 h-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                  </svg>
                  <router-link :to="`/coaches/${coach.coach_uuid}`" class="text-sm text-gray-900 truncate hover:underline">
                    {{ coach.first_name }} {{ coach.last_name }}
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Absent Coaches -->
            <div v-if="absentCoaches.length > 0">
              <h3 class="text-sm font-semibold text-red-700 mb-2">{{ t('eventDetail.absent') }} ({{ absentCoaches.length }})</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                <div v-for="coach in absentCoaches" :key="coach.coach_uuid" class="flex flex-col gap-1 p-2 bg-red-50 border border-red-200 rounded">
                  <div class="flex items-center gap-2">
                    <svg class="w-5 h-5 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                    </svg>
                    <router-link :to="`/coaches/${coach.coach_uuid}`" class="text-sm text-gray-900 truncate hover:underline">
                      {{ coach.first_name }} {{ coach.last_name }}
                    </router-link>
                  </div>
                  <div v-if="coach.absence_reason" class="text-xs text-gray-600 italic ml-7">
                    {{ coach.absence_reason }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Players Attendance Section (hidden for PLAYER role) -->
          <div v-if="currentRole !== 'PLAYER'" class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ t('eventDetail.players') }}</h2>

            <!-- Present Players -->
            <div v-if="presentPlayers.length > 0" class="mb-6">
              <h3 class="text-sm font-semibold text-green-700 mb-2">{{ t('eventDetail.present') }} ({{ presentPlayers.length }})</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                <div v-for="player in presentPlayers" :key="player.player_uuid" class="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded">
                  <svg class="w-5 h-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                  </svg>
                  <router-link :to="`/players/${player.player_uuid}`" class="text-sm text-gray-900 flex-1 truncate hover:underline">
                    {{ player.first_name }} {{ player.last_name }}
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Absent Players -->
            <div v-if="absentPlayers.length > 0">
              <h3 class="text-sm font-semibold text-red-700 mb-2">{{ t('eventDetail.absent') }} ({{ absentPlayers.length }})</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                <div v-for="player in absentPlayers" :key="player.player_uuid" class="flex flex-col gap-1 p-2 bg-red-50 border border-red-200 rounded">
                  <div class="flex items-center gap-2">
                    <svg class="w-5 h-5 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                    </svg>
                    <router-link :to="`/players/${player.player_uuid}`" class="text-sm text-gray-900 flex-1 truncate hover:underline">
                      {{ player.first_name }} {{ player.last_name }}
                    </router-link>
                  </div>
                  <div v-if="player.absence_reason" class="text-xs text-gray-600 italic ml-7">
                    {{ player.absence_reason }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Expected Participants Preview (when attendance not checked) -->
        <template v-else-if="participants">
          <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-6 mt-4">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ t('eventDetail.expectedParticipants') }}</h2>

            <!-- Coaches -->
            <div v-if="participants.coaches && participants.coaches.length > 0" class="mb-4">
              <h3 class="text-sm font-semibold text-gray-700 mb-2">{{ t('eventDetail.coaches') }} ({{ participants.coaches.length }})</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                <div v-for="coach in participants.coaches" :key="coach.uuid" class="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded">
                  <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
                  </svg>
                  <router-link :to="`/coaches/${coach.uuid}`" class="text-sm text-gray-900 truncate hover:underline">
                    {{ coach.first_name }} {{ coach.last_name }}
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Players (hidden for PLAYER role) -->
            <div v-if="currentRole !== 'PLAYER' && participants.players && participants.players.length > 0">
              <h3 class="text-sm font-semibold text-gray-700 mb-2">{{ t('eventDetail.players') }} ({{ participants.players.length }})</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                <div v-for="player in participants.players" :key="player.uuid" class="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded">
                  <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
                  </svg>
                  <router-link :to="`/players/${player.uuid}`" class="text-sm text-gray-900 flex-1 truncate hover:underline">
                    {{ player.first_name }} {{ player.last_name }}
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>
  </main>
</template>
